import { Configuration, PitchPalette } from "../context/ConfigurationContext";
import { Pitch, StrikeZone } from "../types/StrikeZone";

/* Constants */
const ballWidth = 2.9;
const defaultLineWidth = 5.0;
const plateWidth = 17.0;

type XYCoordinate = {
  x: number;
  y: number;
};

const feetToPixels = (feet: number, scale: number): number => {
  return 216 * scale * feet;
};

const inchesToPixels = (inches: number, scale: number): number => {
  return feetToPixels(inches / 12, scale);
};

export const getCorrectPalette = (config: Configuration, pitch: Pitch): PitchPalette => {
  const { inPlay, isStrike } = pitch;
  if (inPlay) {
    return config.palette.inPlay;
  } else {
    return isStrike ? config.palette.strike : config.palette.ball;
  }
};

export const clearCanvas = (canvas: HTMLCanvasElement) => {
  const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
};

export const drawStrikeZone = (canvas: HTMLCanvasElement, strikeZone: StrikeZone, config: Configuration) => {
  const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
  const { szTop, szBottom, szWidth } = strikeZone;

  const lineOffset = (defaultLineWidth * config.scaling.multiplier) / 2;
  const zoneWidth = !!szWidth
    ? inchesToPixels(szWidth, config.scaling.multiplier)
    : inchesToPixels(plateWidth, config.scaling.multiplier);
  const centerX = canvas.width / 2;
  const zoneHeight = feetToPixels(szTop - szBottom, config.scaling.multiplier);
  const startDrawX = centerX - zoneWidth / 2 - lineOffset;
  const startDrawY = canvas.height - feetToPixels(szTop, config.scaling.multiplier) - lineOffset;

  const zoneDrawWidth = zoneWidth - lineOffset;
  const zoneDrawHeight = zoneHeight - lineOffset;

  /* Draw Strike Zone */
  ctx.lineWidth = defaultLineWidth * config.scaling.multiplier;
  ctx.strokeStyle = config.palette.strikeZone;

  ctx.strokeRect(startDrawX, startDrawY, zoneDrawWidth, zoneDrawHeight);

  /* Draw Nine Box */
  ctx.beginPath();
  ctx.lineWidth = defaultLineWidth * config.scaling.multiplier;
  ctx.strokeStyle = config.palette.strikeZone;

  ctx.moveTo(startDrawX + zoneDrawWidth / 3, startDrawY);
  ctx.lineTo(startDrawX + zoneDrawWidth / 3, startDrawY + zoneDrawHeight);

  // Right
  ctx.moveTo(startDrawX + (zoneDrawWidth * 2) / 3, startDrawY);
  ctx.lineTo(startDrawX + (zoneDrawWidth * 2) / 3, startDrawY + zoneDrawHeight);

  // Top
  ctx.moveTo(startDrawX, startDrawY + zoneDrawHeight / 3);
  ctx.lineTo(startDrawX + zoneDrawWidth, startDrawY + zoneDrawHeight / 3);

  // Bottom
  ctx.moveTo(startDrawX, startDrawY + (zoneDrawHeight * 2) / 3);
  ctx.lineTo(startDrawX + zoneDrawWidth, startDrawY + (zoneDrawHeight * 2) / 3);

  ctx.stroke();
};

const drawBaseball = (
  ctx: CanvasRenderingContext2D,
  pitchEnd: XYCoordinate,
  palette: PitchPalette,
  config: Configuration,
) => {
  //draw the ball
  const diameter: number = inchesToPixels(ballWidth / 2, config.scaling.multiplier);
  ctx.beginPath();
  ctx.arc(pitchEnd.x, pitchEnd.y, diameter, 0.0, 2.0 * Math.PI);
  ctx.fillStyle = palette.ball;
  ctx.strokeStyle = palette.ball;
  ctx.fill();
  ctx.stroke();

  drawLeftStitch(ctx, pitchEnd, diameter);
  drawRightStitch(ctx, pitchEnd, diameter);
};

const drawLeftStitch = (ctx: CanvasRenderingContext2D, pitchEnd: XYCoordinate, diameter: number) => {
  drawStitch(ctx, pitchEnd, diameter, true);
};

const drawRightStitch = (ctx: CanvasRenderingContext2D, pitchEnd: XYCoordinate, diameter: number) => {
  drawStitch(ctx, pitchEnd, diameter, false);
};

const drawStitch = (ctx: CanvasRenderingContext2D, pitchEnd: XYCoordinate, diameter: number, isLeft: boolean) => {
  const flipper = isLeft ? 1 : -1;
  const xCoord = pitchEnd.x + (diameter / 2) * flipper + 2 * flipper;
  const startY = pitchEnd.y - diameter + 2;
  const midX = pitchEnd.x - ((diameter / 4) * flipper - 2 * flipper);
  const midY = pitchEnd.y;
  const endY = pitchEnd.y + diameter - 3;

  //draw lines to make it look like a baseball
  ctx.beginPath();
  ctx.bezierCurveTo(xCoord, startY, midX, midY, xCoord, endY);
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();
};

export const drawPitch = (canvas: HTMLCanvasElement, strikeZone: StrikeZone, pitch: Pitch, config: Configuration) => {
  const ctx: CanvasRenderingContext2D = canvas.getContext("2d");

  clearCanvas(canvas);

  const isRightHanded = pitch?.pitchHand === "R";

  const pitchStart: XYCoordinate = {
    x:
      isRightHanded && !config.display.isPitchersView
        ? canvas.width / 2 - 50 * config.scaling.multiplier
        : canvas.width / 2 + 50 * config.scaling.multiplier,
    y: 125 * config.scaling.multiplier,
  };

  const pitchEnd: XYCoordinate = calcPitchEnd(canvas, pitch, config);

  const curvePoint: XYCoordinate = {
    x: pitchEnd.x,
    y: Math.min(300 * config.scaling.multiplier, pitchEnd.y - pitchStart.y),
  };

  const palette: PitchPalette = getCorrectPalette(config, pitch);

  // Draw Pitch Arc
  const ballWidthInPixels = inchesToPixels(ballWidth, config.scaling.multiplier);
  for (let i = 0; i < ballWidthInPixels - 4; i++) {
    ctx.beginPath();
    ctx.bezierCurveTo(
      pitchStart.x,
      pitchStart.y,
      curvePoint.x,
      curvePoint.y,
      pitchEnd.x + 2 - inchesToPixels(ballWidth / 2, config.scaling.multiplier) + i,
      pitchEnd.y,
    );
    ctx.strokeStyle = palette.arch;
    ctx.stroke();
  }

  drawStrikeZone(canvas, strikeZone, config);

  drawBaseball(ctx, pitchEnd, palette, config);
};

const calcPitchEnd = (canvas: HTMLCanvasElement, pitch: Pitch, config: Configuration): XYCoordinate => {
  let { xCoord, zCoord } = pitch;
  const isPitchersView = config.display.isPitchersView;

  const minX = -1.75;
  const maxX = 1.75;
  const minZ = config.display.displayAddtlInfo ? 1.0 : 0.5;
  const maxZ = 4.25;

  // X Coordinate
  if (xCoord < minX) {
    xCoord = minX;
  } else if (xCoord > maxX) {
    xCoord = maxX;
  }

  // Z Coordinate
  if (zCoord < minZ) {
    zCoord = minZ;
  } else if (zCoord > maxZ) {
    zCoord = maxZ;
  }

  return {
    x: canvas.width / 2 + feetToPixels(isPitchersView ? -xCoord : xCoord, config.scaling.multiplier),
    y: canvas.height - feetToPixels(zCoord, config.scaling.multiplier),
  };
};
